import { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { IoCalendarOutline, IoChevronDownOutline, IoChevronUpOutline } from 'react-icons/io5';
import { readerApi, familyApi, reportApi } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import LoadingSpinner from '../components/LoadingSpinner';
import ProgressOverlay from '../components/ProgressOverlay';
import ReaderPickerSheet from '../components/reporting/ReaderPickerSheet';
import ChapterCalendarSelector from '../components/reporting/ChapterCalendarSelector';
import BookSelector from '../components/reporting/BookSelector';
import ReadingDetailsForm from '../components/reporting/ReadingDetailsForm';
import RawChapterReport from '../components/reporting/RawChapterReport';
import ReadingSheet from '../components/reading/ReadingSheet';
import UndoModal from '../components/UndoModal';
import LogMoment from '../components/log/LogMoment';
import ProgressRecap from '../components/log/ProgressRecap';
import { hasSimplifiedSetup } from '../utils/userSetup';
import { localDateOnly } from '../utils/localDate';
import { formatDateChip, submitLabel, submitLog } from '../utils/logSubmit';
import {
  pickReader,
  setLastReaderId,
  getLastLogMode,
  setLastLogMode
} from '../utils/localPreferences';
import './ReportReadingPage.css';

const EMPTY_PROGRESS = { stage: '', current: 0, total: 0, message: '' };

const ReportReadingPage = () => {
  const { user } = useAuth();
  const [readers, setReaders] = useState([]);
  const [families, setFamilies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [submissionProgress, setSubmissionProgress] = useState(EMPTY_PROGRESS);
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState('');
  const [showUndoModal, setShowUndoModal] = useState(false);
  const [moment, setMoment] = useState(null);
  const [momentReportIds, setMomentReportIds] = useState([]);
  const [recap, setRecap] = useState(null);

  const [mode, setMode] = useState(() => getLastLogMode(user?.id));
  const [readerMode, setReaderMode] = useState('primary'); // 'primary' | 'family' | 'specific'
  const [selectedReaders, setSelectedReaders] = useState([]);
  const [selectedFamily, setSelectedFamily] = useState(null);
  const [selectedChapters, setSelectedChapters] = useState([]);
  const [selectedBooks, setSelectedBooks] = useState([]);
  const [readDate, setReadDate] = useState(localDateOnly());
  const [notes, setNotes] = useState('');
  const [readingDetails, setReadingDetails] = useState({});
  const [showMore, setShowMore] = useState(false);
  const [showBulk, setShowBulk] = useState(false);
  const dateInputRef = useRef(null);

  useEffect(() => {
    const load = async () => {
      try {
        const [readersRes, familiesRes] = await Promise.all([
          readerApi.getReaders(),
          familyApi.getFamilies()
        ]);
        const loadedReaders = readersRes.data.readers || [];
        setReaders(loadedReaders);
        setFamilies(familiesRes.data.families || []);

        // The reader they last logged for, else the primary reader.
        const initial = pickReader(loadedReaders, user?.id);
        if (initial) {
          setSelectedReaders([initial]);
          setReaderMode(initial.isPrimary ? 'primary' : 'specific');
        }
      } catch (err) {
        console.error('Failed to load data:', err);
        setError('Failed to load the log form. Please try again.');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [user?.id]);

  const changeMode = (next) => {
    setMode(next);
    setLastLogMode(user?.id, next);
  };

  const pickMe = () => {
    setReaderMode('primary');
    setSelectedFamily(null);
    setSelectedReaders(readers.filter(r => r.isPrimary));
  };

  const pickFamily = (family) => {
    setReaderMode('family');
    setSelectedFamily(family);
    setSelectedReaders(family?.readers || []);
  };

  const toggleReader = (reader) => {
    // The first checkbox tap leaves "Just me" or a family for a custom list.
    const base = readerMode === 'specific' ? selectedReaders : [];
    setReaderMode('specific');
    setSelectedFamily(null);
    setSelectedReaders(
      base.some(r => r.id === reader.id) ? base.filter(r => r.id !== reader.id) : [...base, reader]
    );
  };

  const label = submitLabel({ mode, chapterIds: selectedChapters, books: selectedBooks });
  const canSubmit = !submitting && selectedReaders.length > 0 && label !== null;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!canSubmit) return;

    try {
      setSubmitting(true);
      setError(null);
      setSuccessMessage('');
      setRecap(null);

      const result = await submitLog({
        mode,
        readers: selectedReaders,
        readerMode,
        family: selectedFamily,
        chapterIds: selectedChapters,
        books: selectedBooks,
        readDate,
        notes,
        details: readingDetails,
        onProgress: (patch) => setSubmissionProgress(prev => ({ ...prev, ...patch })),
        api: reportApi
      });

      setSuccessMessage(result.message);
      setMoment(result.moment);
      setRecap(result.recap);
      setMomentReportIds(result.reportIds);
      if (selectedReaders.length === 1) setLastReaderId(user?.id, selectedReaders[0].id);

      setSelectedChapters([]);
      setSelectedBooks([]);
      setNotes('');
    } catch (err) {
      console.error('Failed to submit report:', err);
      setError(err.response?.data?.error || 'Failed to log that reading. Please try again.');
    } finally {
      setSubmitting(false);
      setSubmissionProgress(EMPTY_PROGRESS);
    }
  };

  const handleUndoSuccess = (message, summary) => {
    setError(null);
    setRecap(null);
    setSuccessMessage(`${message}. Removed logs for: ${summary.readers.join(', ')}`);
  };

  if (loading) {
    return (
      <div className="report-page loading">
        <LoadingSpinner size="large" message="Loading..." />
      </div>
    );
  }

  const simplified = hasSimplifiedSetup(readers, families);

  return (
    <div className="report-page">
      <header className="report-header">
        <h1>Log reading</h1>
        <div className="report-date">
          <button
            type="button"
            className="report-date-chip"
            onClick={() => dateInputRef.current?.showPicker?.() ?? dateInputRef.current?.focus()}
          >
            <IoCalendarOutline size={16} aria-hidden="true" />
            {formatDateChip(readDate)}
          </button>
          <input
            ref={dateInputRef}
            type="date"
            className="report-date-input"
            aria-label="Date read"
            value={readDate}
            max={localDateOnly()}
            onChange={(e) => e.target.value && setReadDate(e.target.value)}
            tabIndex={-1}
          />
        </div>
      </header>

      {readers.length === 0 && (
        <p className="report-alert report-alert--error">
          Add a reader under Family → Readers before logging what you read.{' '}
          <Link to="/family#readers">Go to Family → Readers</Link>
        </p>
      )}

      {error && <p className="report-alert report-alert--error" role="alert">{error}</p>}

      {successMessage && (
        <p className="report-alert report-alert--success" role="status">
          {successMessage}{' '}
          <button type="button" className="report-link" onClick={() => setShowUndoModal(true)}>
            Undo
          </button>
        </p>
      )}

      {/* Book logs get no moment sheet, so their recap shows here. It stays
          after the sheet closes, too. */}
      {successMessage && recap && !moment && (
        <div className="report-recap">
          <ProgressRecap recap={recap} />
        </div>
      )}

      <form onSubmit={handleSubmit} className={`report-form ${submitting ? 'submitting' : ''}`}>
        {!simplified && readers.length > 0 && (
          <ReaderPickerSheet
            readers={readers}
            families={families}
            readerMode={readerMode}
            selectedReaders={selectedReaders}
            selectedFamily={selectedFamily}
            onPickMe={pickMe}
            onPickFamily={pickFamily}
            onToggleReader={toggleReader}
          />
        )}

        <div className="report-mode" role="tablist" aria-label="What did you read?">
          {[['chapters', 'Chapters'], ['books', 'Whole books']].map(([key, text]) => (
            <button
              key={key}
              type="button"
              role="tab"
              aria-selected={mode === key}
              className={`report-mode-tab ${mode === key ? 'active' : ''}`}
              onClick={() => changeMode(key)}
            >
              {text}
            </button>
          ))}
        </div>

        {mode === 'chapters' ? (
          <ChapterCalendarSelector
            selectedChapters={selectedChapters}
            onChaptersChange={setSelectedChapters}
          />
        ) : (
          <BookSelector selectedBooks={selectedBooks} onBooksChange={setSelectedBooks} />
        )}

        <div className="report-more">
          <button
            type="button"
            className="report-more-toggle"
            onClick={() => setShowMore(!showMore)}
            aria-expanded={showMore}
          >
            Note &amp; details
            {showMore ? <IoChevronUpOutline size={16} aria-hidden="true" /> : <IoChevronDownOutline size={16} aria-hidden="true" />}
          </button>
          {/* Kept mounted while closed: the details form applies the user's saved defaults on mount. */}
          <div hidden={!showMore} className="report-more-body">
            <label htmlFor="notes" className="visually-hidden">Notes</label>
            <textarea
              id="notes"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Any thoughts or reflections (optional)"
              maxLength={1000}
              rows={3}
            />
            <ReadingDetailsForm
              formData={readingDetails}
              onChange={setReadingDetails}
              showDetails
              onToggleDetails={() => setShowMore(false)}
              compact
            />
          </div>
        </div>

        <button type="button" className="report-link report-bulk-link" onClick={() => setShowBulk(true)}>
          Log a total count instead
        </button>

        <div className="report-submit">
          <button type="submit" className="report-submit-button" disabled={!canSubmit}>
            {submitting ? 'Logging…' : label || 'Choose what you read'}
          </button>
          {submitting && (
            <p className="report-submit-hint">Large logs may take a few minutes. Please don't close this window.</p>
          )}
        </div>
      </form>

      <ReadingSheet open={showBulk} onClose={() => setShowBulk(false)} title="Log a total count">
        <RawChapterReport
          readers={readers}
          onSuccess={(report) => {
            setShowBulk(false);
            setSuccessMessage(`Logged ${report.chapterCount} chapter${report.chapterCount === 1 ? '' : 's'}.`);
          }}
        />
      </ReadingSheet>

      <ProgressOverlay isVisible={submitting} progress={submissionProgress} />

      {moment && (
        <LogMoment
          moment={moment}
          recap={recap}
          onClose={() => setMoment(null)}
          onUndo={() => {
            setMoment(null);
            setShowUndoModal(true);
          }}
          onSaveReflection={momentReportIds.length > 0
            ? (text) => Promise.all(momentReportIds.map(id => reportApi.updateReport(id, { notes: text })))
            : undefined}
        />
      )}

      <UndoModal
        isOpen={showUndoModal}
        onClose={() => setShowUndoModal(false)}
        onUndoSuccess={handleUndoSuccess}
      />
    </div>
  );
};

export default ReportReadingPage;
